import React, { useEffect, useState } from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getUniversalMeasurementByProject } from '../../services/api/implementation/impl';

/*
 * EditPricing — allows editing an existing pricing row.
 *
 * Three rate types are supported:
 *
 *  1. Cookie-based  (e.g. sq/ft Based, sq/mt Based)
 *     - User edits price directly in cookie units.
 *     - No conversion needed on save or prefill.
 *
 *  2. Accommodation-based
 *     - User edits a flat price (not per area unit).
 *     - No conversion needed on save or prefill.
 *
 *  3. Universal Measurement (UM) based  (e.g. tago Based)
 *     - Backend stores price in cookie units (e.g. ₹100/sq/ft).
 *     - On PREFILL  → reverse-convert: stored price × per_unit = ₹1000/tago shown to user.
 *     - On SAVE     → forward-convert: entered price ÷ per_unit = ₹100/sq/ft sent to backend.
 *     - universal_measurement_id is always included in the payload for UM rows.
 *
 * Conversion rule (UM type always matches cookie type — enforced by the Create dropdown filter):
 *     forward  : price_per_cookie_unit = price_per_um_unit / per_unit
 *     reverse  : price_per_um_unit     = price_per_cookie_unit * per_unit
 */

const EditPricing = ({
  handleEdit,
  selectedTableType,         // 'sqft' | 'accommodation' — what the table was filtered to
  singlePricingData,         // full pricing object fetched from server for the row being edited
  pricingType,               // 'sqft' | 'accommodation'
  selectedAccommodationType, // 'INDEPENDENT' | 'GROUP'
  onClose,
  isSubmitting
}) => {
  const userId = Cookies.get('userId');

  // Cookie-based measurement unit for this company — 'sq/ft' or 'sq/mt'
  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  const measurementTypeWithoutSlash = measurementType.replace('/', '');
  const { id: projectId } = useParams();

  // Fetch all universal measurements for the project so we can look up the one used in this row
  const { data: universalMeasurements = [] } = useQuery({
    queryKey: ['universalMeasurementsByProject', projectId],
    queryFn: () => getUniversalMeasurementByProject(projectId),
    enabled: !!projectId
  });

  /*
   * Determine if this pricing row was created using a universal measurement.
   * singlePricingData.universal_measurement_id is set only for UM-based rows.
   * We look up the full UM object so we can access its name and per_unit.
   */
  const umId = singlePricingData?.universal_measurement_id;
  const selectedUm = umId ? universalMeasurements.find((u) => u.id === umId) : null;
  const isUmBased = !!selectedUm;

  /*
   * reverseConvertUmPrice — used when PREFILLING the form.
   * The backend stores prices in cookie units (e.g. ₹100/sq/ft).
   * We need to show the user the price in UM units (e.g. ₹1000/tago).
   *
   * Formula: display_price = stored_price × per_unit
   * Example: 100 (stored) × 10 (per_unit) = 1000/tago shown in input
   * Precision: toFixed(2) keeps UI display clean and avoids float artifacts like 1000.0000008.
   */
  const reverseConvertUmPrice = (storedPrice) => {
    const perUnit = parseFloat(selectedUm?.per_unit) || 1;
    return parseFloat((storedPrice * perUnit).toFixed(2));
  };

  /*
   * convertUmPrice — used when SAVING the form.
   * User enters price in UM units (e.g. ₹1000/tago).
   * We need to send price in cookie units (e.g. ₹100/sq/ft) to the backend.
   *
   * Formula: cookie_price = entered_price / per_unit
   * Example: 1000 (entered) / 10 (per_unit) = 100/sq/ft sent to backend
   * Precision: toFixed(8) avoids integer rounding that causes display errors.
   */
  const convertUmPrice = (rawPrice) => {
    const perUnit = parseFloat(selectedUm?.per_unit) || 1;
    return parseFloat((rawPrice / perUnit).toFixed(8));
  };

  // Display helper: keep accommodation-mode values visible with 2 decimals in form inputs.
  const formatAccommodationDisplayPrice = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    mixpanel.track('Edit Pricing Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  /*
   * Prefill form fields when singlePricingData loads.
   *
   * For UM-based rows: reverse-convert stored prices back to UM units so the user
   * sees the value they originally entered (e.g. ₹1000/tago, not ₹100/sq/ft).
   *
   * For cookie-based / accommodation rows: use stored values as-is.
   */
  useEffect(() => {
    if (!singlePricingData) return;

    const lf = singlePricingData.loading_factor ?? '';
    setLoadingFactor(String(lf));

    if (selectedAccommodationType === 'INDEPENDENT') {
      // INDEPENDENT type has separate plot price and construction price
      const plotVal = isUmBased
        ? reverseConvertUmPrice(singlePricingData.plote_price || 0)
        : selectedTableType === 'accommodation'
          ? formatAccommodationDisplayPrice(singlePricingData.plote_price)
          : singlePricingData.plote_price || '';
      const constructionVal = isUmBased
        ? reverseConvertUmPrice(singlePricingData.construction_price || 0)
        : selectedTableType === 'accommodation'
          ? formatAccommodationDisplayPrice(singlePricingData.construction_price)
          : singlePricingData.construction_price || '';
      reset({
        plote_price: plotVal,
        construction_price: constructionVal,
        loading_factor: lf
      });
    } else {
      // GROUP type has a single price field
      const rateVal = isUmBased
        ? reverseConvertUmPrice(singlePricingData.price || 0)
        : selectedTableType === 'accommodation'
          ? formatAccommodationDisplayPrice(singlePricingData.price)
          : singlePricingData.price || '';
      reset({
        rate: rateVal,
        loading_factor: lf
      });
    }
  }, [singlePricingData, selectedAccommodationType, reset, isUmBased, selectedUm]);

  /*
   * onSubmit — builds the payload and calls handleEdit.
   *
   * For UM-based rows:
   *   - Converts entered price back to cookie units using convertUmPrice.
   *   - Adds universal_measurement_id so the backend knows which UM was used.
   *
   * For cookie-based rows:
   *   - Sends price as-is (already in cookie units).
   *
   * mode is always 'sqft' for area-based (cookie + UM) and 'accommodation' for flat price.
   */
  const onSubmit = (data) => {
    const processedData = {
      accommodation_id: singlePricingData.accommodation_id,
      project_id: singlePricingData.project_id,
      mode: selectedTableType === 'sqft' || isUmBased ? 'sqft' : 'accommodation',
      // Only include universal_measurement_id for UM-based rows
      ...(isUmBased ? { universal_measurement_id: selectedUm.id } : {})
    };

    if (selectedAccommodationType === 'INDEPENDENT') {
      const rawPlot = parseInt(data.plote_price, 10) || 0;
      const rawConstruction = parseInt(data.construction_price, 10) || 0;
      // UM: convert UM-unit price → cookie-unit price before sending
      processedData.plote_price = isUmBased ? convertUmPrice(rawPlot) : rawPlot;
      processedData.construction_price = isUmBased ? convertUmPrice(rawConstruction) : rawConstruction;
      if (processedData.mode === 'sqft') {
        processedData.loading_factor = parseFloat(data.loading_factor, 10) || 0;
      }
    } else {
      const rawRate = parseInt(data.rate, 10) || 0;
      // UM: convert UM-unit price → cookie-unit price before sending
      processedData.price = isUmBased ? convertUmPrice(rawRate) : rawRate;
      if (processedData.mode === 'sqft') {
        processedData.loading_factor = parseFloat(data.loading_factor, 10) || 0;
      }
    }

    console.log('Edit Accommodation Pricing - Submit Payload:', processedData);
    handleEdit(processedData);
  };

  const [loadingFactor, setLoadingFactor] = useState('');

  // Caps loading factor at 100 and keeps form state in sync
  const handleLoadingFactorChange = (e) => {
    let inputVal = e;
    inputVal = inputVal.replace(/\D/g, '');
    const numericVal = parseInt(inputVal, 10);
    if (!isNaN(numericVal) && numericVal > 100) {
      inputVal = '100';
      toast.error('Loading factor cannot exceed 100');
    }
    setLoadingFactor(inputVal);
    setValue('loading_factor', inputVal);
  };

  const accommodation = singlePricingData?.accommodation;

  /*
   * rateTypeLabel — shown as a read-only field in the form header.
   * - UM row:            "tago Based"
   * - Cookie sqft row:   "sq/ft Based" (or "sq/mt Based")
   * - Accommodation row: "Accommodation Based"
   */
  const rateTypeLabel = isUmBased
    ? `${selectedUm.name} Based`
    : selectedTableType === 'sqft'
    ? `${measurementType} Based`
    : 'Accommodation Based';

  const buildUpArea = accommodation?.buildUpArea ?? accommodation?.build_up_area ?? '—';
  const plotArea = accommodation?.plotArea ?? accommodation?.plot_area ?? '—';

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="flex flex-col justify-between items-start gap-4">
            <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
              Rate Details
            </span>
          </div>

          {/* Read-only info row: Rate Type | Accommodation Type | Accommodation Name */}
          <div className="flex gap-4 mt-4 items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate Type
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {rateTypeLabel}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Type
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {selectedAccommodationType || '—'}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Name
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {accommodation?.name ?? '—'}
              </div>
            </div>
          </div>

          {/* Read-only area info */}
          <div className="flex gap-4 mt-4 items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Build up area
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {buildUpArea}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plot area
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {plotArea}
              </div>
            </div>
          </div>

          {/* Loading factor — shown for cookie-based sqft rows AND UM-based rows */}
          {(selectedTableType === 'sqft' || isUmBased) && (
            <div className="flex justify-between mt-4">
              <FormInput
                width={49}
                label="Loading Factor (%)"
                register={register}
                register_key="loading_factor"
                type="text"
                onType={handleLoadingFactorChange}
                defaultValuePre={loadingFactor}
                errors={errors.loading_factor}
              />
            </div>
          )}

          {/*
           * Editable price fields.
           * INDEPENDENT: separate Plot Price + Construction Price inputs.
           * GROUP:       single Rate input.
           *   - UM row:   label shows "Rate per tago", value is prefilled in tago units.
           *   - sqft row: label shows "Rate per sq/ft", value is prefilled in sq/ft units.
           *   - acc row:  label shows "Price", flat value.
           */}
          <div className="flex w-full justify-between mt-4">
            {selectedAccommodationType === 'INDEPENDENT' ? (
              <div className="flex justify-between mt-4 w-full gap-4">
                <FormInput
                  width={49}
                  label="Plot Price"
                  register={register}
                  register_key="plote_price"
                  isRequired
                  type="text"
                />
                <FormInput
                  width={49}
                  label="Construction Price"
                  register={register}
                  register_key="construction_price"
                  type="text"
                />
              </div>
            ) : (
              <FormInput
                width={49}
                label={
                  isUmBased
                    ? `Rate per ${selectedUm.name}`
                    : selectedTableType === 'sqft'
                    ? `Rate per ${measurementType}`
                    : 'Price'
                }
                register_key="rate"
                isRequired
                register={register}
                type="text"
              />
            )}
          </div>

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button type="button" displayType="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditPricing;
